/**
 * Shooting Stars Module
 * Renders fast-moving shooting stars that streak across the sky with persistent trails and particles
 */

export interface TrailPoint {
  x: number;
  y: number;
  age: number; // Age in seconds (0 = newest)
  particleAge?: number; // For particle effects
}

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  // Spiraling properties
  angle: number; // Current angle in spiral
  angularVelocity: number; // Rotation speed (radians per second)
  spiralRadius: number; // Distance from center of spiral
  spiralSpeed: number; // Outward expansion speed
  centerX: number; // Center point of spiral (where particle started)
  centerY: number;
  life: number;
  maxLife: number;
  size: number;
}

export interface ShootingStar {
  // Starting position
  startX: number;
  startY: number;
  // Current position
  x: number;
  y: number;
  // Base velocity (px/s) - used for easing calculation
  baseVx: number;
  baseVy: number;
  // Current velocity (px/s) - modified by easing
  vx: number;
  vy: number;
  // Speed multiplier (random variation per star)
  speedMultiplier: number;
  // Easing properties
  age: number; // Age in seconds (0 to maxLife)
  easingType: 'easeInOutExpo' | 'easeOutCirc'; // Easing function type
  // Trail properties
  trailPoints: TrailPoint[]; // History of positions for persistent trail
  particles: Particle[]; // Particles along the trail
  maxTrailLength: number;
  // Life cycle
  life: number;
  maxLife: number;
  trailFadeTime: number; // Time for trail to fade after star dies (seconds)
  glowFadeTime: number; // Time for glow to fade after star dies (1 second)
  glowAge: number; // Age of glow after star dies (0 to glowFadeTime)
  isDead: boolean; // Whether star has died but trail is still fading
  // Visual properties
  brightness: number;
  color: { r: number; g: number; b: number };
  size: number;
}

export interface ColorPalette {
  primary: { r: number; g: number; b: number };
  secondary: { r: number; g: number; b: number };
  accent: { r: number; g: number; b: number };
}

export interface ShootingStarsState {
  stars: ShootingStar[];
  spawnTimer: number;
  spawnInterval: number;
  lastSpawnTime: number;
  colorPalette?: ColorPalette;
}

export function initShootingStars(canvas: HTMLCanvasElement, colorPalette?: ColorPalette): ShootingStarsState {
  return {
    stars: [],
    spawnTimer: 0,
    spawnInterval: 6000 + Math.random() * 8000, // Spawn every 6-14 seconds (4x rarer)
    lastSpawnTime: performance.now(),
    colorPalette,
  };
}

// Manually spawn a shooting star (for trigger button)
export function spawnShootingStar(state: ShootingStarsState, canvas: HTMLCanvasElement): void {
  state.stars.push(createShootingStar(canvas, state.colorPalette));
}

function createShootingStar(canvas: HTMLCanvasElement, colorPalette?: ColorPalette): ShootingStar {
  // Get device pixel ratio for coordinate conversion
  // All coordinates must be in device pixels for consistency
  const dpr = window.devicePixelRatio || 1;

  // Get viewport dimensions in DEVICE PIXELS for spawning in visible area
  const viewportHeight = window.innerHeight * dpr;
  const viewportWidth = window.innerWidth * dpr;

  // Calculate screen diagonal to determine how far star should travel
  const diagonal = Math.sqrt(canvas.width * canvas.width + canvas.height * canvas.height);
  const halfScreen = diagonal * 0.5;
  const targetDistance = halfScreen + Math.random() * (diagonal * 0.3);

  // Shooting stars can spawn from two edges:
  // 1. Left edge: X = off-screen left, Y = 0 to 50% of viewport
  // 2. Top edge: X = 0 to 75% of viewport, Y = off-screen top
  // This creates variety while keeping the 45-degree down-right trajectory

  let startX: number;
  let startY: number;

  // Randomly choose spawn edge (50% left, 50% top)
  // All coordinates in device pixels
  if (Math.random() < 0.5) {
    // Spawn from left edge
    startX = (-10 - Math.random() * 30) * dpr; // Just off the left edge
    startY = Math.random() * (viewportHeight * 0.5); // Top 50% of viewport height
  } else {
    // Spawn from top edge
    startX = Math.random() * (viewportWidth * 0.75); // Left 75% of viewport width
    startY = (-10 - Math.random() * 30) * dpr; // Just off the top edge
  }

  // Move at 45 degree angle (down and to the right)
  const baseAngle = Math.PI / 4; // 45 degrees
  const angleVariation = 0.05; // Small variation
  const angle = baseAngle + (Math.random() - 0.5) * angleVariation;

  // Calculate velocity to cover target distance in maxLife seconds
  // Lifespan reduced by half, speed increased by 30%
  const maxLife = 2.5 + Math.random() * 1.5; // 2.5-4 seconds (half of previous 5-8 seconds)

  // Random speed multiplier for variation (0.7x to 1.3x), then add 30% speed increase
  const speedMultiplier = (0.7 + Math.random() * 0.6) * 1.3; // 30% faster
  const baseSpeed = (targetDistance / maxLife) * speedMultiplier;

  const baseVx = Math.cos(angle) * baseSpeed;
  const baseVy = Math.sin(angle) * baseSpeed;

  // Random easing type for natural variation with increased initial acceleration
  const easingTypes: ('easeInOutExpo' | 'easeOutCirc')[] = ['easeInOutExpo', 'easeOutCirc'];
  const easingType = easingTypes[Math.floor(Math.random() * easingTypes.length)];

  // Color variation - use palette if provided, otherwise default white/blue/yellow/orange
  const colorRand = Math.random();
  let color = { r: 255, g: 255, b: 255 };

  if (colorPalette) {
    // Use Ember color palette - mostly primary red, some variations
    if (colorRand < 0.6) {
      // Primary Ember red (60% of stars)
      const variation = 0.15; // 15% variation
      color = {
        r: Math.max(0, Math.min(255, colorPalette.primary.r + (Math.random() - 0.5) * variation * 255)),
        g: Math.max(0, Math.min(255, colorPalette.primary.g + (Math.random() - 0.5) * variation * 255)),
        b: Math.max(0, Math.min(255, colorPalette.primary.b + (Math.random() - 0.5) * variation * 255)),
      };
    } else if (colorRand < 0.85) {
      // Secondary red-orange (25% of stars)
      const variation = 0.2;
      color = {
        r: Math.max(0, Math.min(255, colorPalette.secondary.r + (Math.random() - 0.5) * variation * 255)),
        g: Math.max(0, Math.min(255, colorPalette.secondary.g + (Math.random() - 0.5) * variation * 255)),
        b: Math.max(0, Math.min(255, colorPalette.secondary.b + (Math.random() - 0.5) * variation * 255)),
      };
    } else {
      // Accent orange-yellow (15% of stars)
      const variation = 0.25;
      color = {
        r: Math.max(0, Math.min(255, colorPalette.accent.r + (Math.random() - 0.5) * variation * 255)),
        g: Math.max(0, Math.min(255, colorPalette.accent.g + (Math.random() - 0.5) * variation * 255)),
        b: Math.max(0, Math.min(255, colorPalette.accent.b + (Math.random() - 0.5) * variation * 255)),
      };
    }
  } else {
    // Default color variation - mostly white/blue, some yellow/orange
    if (colorRand < 0.7) {
      // White-blue
      color = {
        r: 200 + Math.random() * 55,
        g: 220 + Math.random() * 35,
        b: 255,
      };
    } else if (colorRand < 0.9) {
      // Yellow-white
      color = {
        r: 255,
        g: 240 + Math.random() * 15,
        b: 200 + Math.random() * 55,
      };
    } else {
      // Orange-red (rare)
      color = {
        r: 255,
        g: 180 + Math.random() * 50,
        b: 100 + Math.random() * 50,
      };
    }
  }

  const trailFadeTime = 0.3 + Math.random() * 0.2; // Trail fades quickly: 0.3-0.5 seconds after star dies
  const glowFadeTime = 1.0; // Glow fades for 1 second after star dies
  const maxTrailLength = baseSpeed * maxLife * 0.25; // Trail covers 25% of total distance (reduced from 40%)

  return {
    startX,
    startY,
    x: startX,
    y: startY,
    baseVx,
    baseVy,
    vx: baseVx, // Initial velocity equals base velocity
    vy: baseVy,
    speedMultiplier,
    age: 0,
    easingType,
    trailPoints: [{ x: startX, y: startY, age: 0 }], // Initialize with starting position
    particles: [],
    maxTrailLength,
    life: 1.0,
    maxLife,
    trailFadeTime,
    glowFadeTime,
    glowAge: 0,
    isDead: false,
    brightness: colorPalette
      ? Math.random() * 0.15 + 0.85 // Bolder for Ember: 0.85-1.0 (was 0.75-0.95)
      : Math.random() * 0.2 + 0.75, // Reduced by 0.05 (0.75-0.95, was 0.8-1.0)
    color,
    size: colorPalette
      ? Math.random() * 1.0 + 1.0 // Bolder for Ember: 1.0-2.0px (was 0.7-1.5px)
      : Math.random() * 0.8 + 0.7, // 0.7-1.5px head size (smaller)
  };
}

// Easing functions for natural shooting star movement with increased initial acceleration
// These functions return a speed multiplier: 1 = full speed, 0 = stopped
// t goes from 0 (start) to 1 (end of life)
// All functions start fast and decelerate smoothly
function easeInOutExpo(t: number): number {
  // Exponential ease-in-out: starts very fast, decelerates smoothly
  // t=0 should return 1 (full speed), t=1 should return ~0 (stopped)
  if (t === 0) return 1;
  if (t === 1) return 0;

  // Standard ease-in-out-expo curve (starts slow, speeds up, slows down)
  let standard: number;
  if (t < 0.5) {
    standard = Math.pow(2, 20 * t - 10) / 2;
  } else {
    standard = (2 - Math.pow(2, -20 * t + 10)) / 2;
  }

  // Invert and adjust: start at 1, end near 0, with strong initial speed
  // Use the "in" part more to emphasize initial speed
  return 1 - standard;
}

function easeOutCirc(t: number): number {
  // Circular ease-out: starts fast, decelerates smoothly
  // t=0 should return 1 (full speed), t=1 should return 0 (stopped)
  // Standard ease-out-circ: starts at 0, ends at 1
  const standard = Math.sqrt(1 - Math.pow(t - 1, 2));
  // Invert: start at 1, end at 0
  return 1 - standard;
}

function applyEasing(easingType: 'easeInOutExpo' | 'easeOutCirc', t: number): number {
  // All shooting stars start fast and decelerate (like real ones)
  // t goes from 0 (start) to 1 (end)
  // Return value represents speed multiplier: 1 = full speed, 0 = stopped
  switch (easingType) {
    case 'easeInOutExpo':
      return easeInOutExpo(t);
    case 'easeOutCirc':
      return easeOutCirc(t);
    default:
      return easeOutCirc(t);
  }
}

function createParticle(
  x: number,
  y: number,
  vx: number,
  vy: number,
  starSpeed: number,
  starSize: number
): Particle {
  // Calculate star's speed magnitude for particle intensity
  const speedMagnitude = Math.sqrt(vx * vx + vy * vy);

  // Spiraling properties - more intense for faster/larger stars
  const spiralIntensity = Math.min(1, (speedMagnitude / 500) + (starSize / 2)); // Scale based on speed and size
  const angularVelocity = (0.5 + Math.random() * 1.5) * (0.5 + spiralIntensity * 1.5); // 0.5-3 rad/s, faster for intense stars
  const spiralSpeed = (5 + Math.random() * 10) * (0.5 + spiralIntensity * 0.5); // Outward expansion speed
  const initialAngle = Math.random() * Math.PI * 2; // Random starting angle

  // Base velocity from star's movement (reduced)
  const baseVx = vx * 0.05;
  const baseVy = vy * 0.05;

  return {
    x,
    y,
    vx: baseVx, // Base velocity (will be modified by spiral)
    vy: baseVy,
    angle: initialAngle,
    angularVelocity,
    spiralRadius: 0, // Start at center
    spiralSpeed,
    centerX: x,
    centerY: y,
    life: 1.0,
    maxLife: 0.8 + Math.random() * 0.7, // 0.8-1.5 seconds (longer for better visibility)
    size: (Math.random() * 0.8 + 0.4) * (0.8 + spiralIntensity * 0.4), // 0.4-1.2px, larger for intense stars
  };
}

export function updateShootingStars(
  state: ShootingStarsState,
  canvas: HTMLCanvasElement,
  deltaTime: number
): void {
  // Spawn new shooting stars
  const currentTime = performance.now();
  if (currentTime - state.lastSpawnTime >= state.spawnInterval) {
    state.stars.push(createShootingStar(canvas, state.colorPalette));
    state.lastSpawnTime = currentTime;
    state.spawnInterval = 6000 + Math.random() * 8000; // Random interval (6-14 seconds, 4x rarer)
  }

  const deltaSeconds = deltaTime / 1000;

  // Update existing shooting stars
  for (let i = state.stars.length - 1; i >= 0; i--) {
    const star = state.stars[i];

    if (!star.isDead) {
      // Update age for easing calculation
      star.age += deltaSeconds;

      // Calculate easing factor (0 to 1 over lifetime)
      const normalizedAge = Math.min(star.age / star.maxLife, 1);
      const easingFactor = applyEasing(star.easingType, normalizedAge);

      // Apply easing to velocity - all stars start fast and decelerate to nearly 0 (like real shooting stars)
      // The easing functions return speed multiplier: 1 = full speed, 0 = stopped
      // Boost initial speed for increased acceleration effect (already 30% faster from baseSpeed)
      const initialSpeedBoost = 1.1 + Math.random() * 0.2; // Start 10-30% faster than base speed
      const finalSpeedRatio = 0.02 + Math.random() * 0.03; // End at nearly 0 (2-5% of boosted speed)
      // easingFactor goes from 1 (start) to 0 (end), so we map it to boosted speed range
      // Ensure it decelerates to nearly 0 right before dying
      const velocityMultiplier = initialSpeedBoost * (easingFactor * (1 - finalSpeedRatio) + finalSpeedRatio);

      // Update velocity based on easing
      star.vx = star.baseVx * velocityMultiplier;
      star.vy = star.baseVy * velocityMultiplier;

      // Star is still alive - continue moving
      const prevX = star.x;
      const prevY = star.y;

      star.x += star.vx * deltaSeconds;
      star.y += star.vy * deltaSeconds;

      // Add current position to trail (every frame)
      star.trailPoints.push({ x: star.x, y: star.y, age: 0 });

      // Limit trail length (keep last 50 points for shorter trail)
      if (star.trailPoints.length > 50) {
        star.trailPoints.shift();
      }

      // Age all trail points
      for (const point of star.trailPoints) {
        point.age += deltaSeconds;
      }

      // Spawn particles along the trail - much more frequent spawning
      // As life decreases (from 1.0 to 0), particle spawn rate increases
      const lifeRemaining = star.life; // 1.0 = full life, 0 = dead
      const particleSpawnRate = 0.7 + (1 - lifeRemaining) * 0.25; // 70% at start, up to 95% as it dies (increased from 40-90%)

      // Calculate star's speed and size for particle intensity
      const starSpeed = Math.sqrt(star.vx * star.vx + star.vy * star.vy);
      const isLargeFastStar = starSpeed > 300 && star.size > 1.0; // Large and fast stars get more particles

      if (Math.random() < particleSpawnRate) {
        const trailIndex = Math.floor(Math.random() * star.trailPoints.length);
        if (trailIndex > 0) {
          const trailPoint = star.trailPoints[trailIndex];
          // Create significantly more particles as star dies, and even more for large/fast stars
          let particleCount: number;
          if (isLargeFastStar) {
            // Large/fast stars: 3-6 particles at start, 6-10 as they die
            particleCount = lifeRemaining > 0.5
              ? Math.floor(Math.random() * 3) + 3  // 3-5 at start (increased)
              : Math.floor(Math.random() * 4) + 6;   // 6-9 as it dies (increased)
          } else {
            // Normal stars: 2-4 at start, 4-6 as they die
            particleCount = lifeRemaining > 0.5
              ? Math.floor(Math.random() * 2) + 2   // 2-3 at start (increased)
              : Math.floor(Math.random() * 2) + 4;   // 4-5 as it dies (increased)
          }

          for (let p = 0; p < particleCount; p++) {
            star.particles.push(createParticle(trailPoint.x, trailPoint.y, star.vx, star.vy, starSpeed, star.size));
          }
        }
      }

      // Update life (decrease over time)
      star.life -= deltaTime / (star.maxLife * 1000);

      // Star continues moving even as it fades - only mark as dead when life reaches 0
      if (star.life <= 0) {
        star.isDead = true;
        star.life = 0;
        star.glowAge = 0; // Start glow fade timer
      }
    } else {
      // Star is dead, but trail and glow are still fading
      // Update glow age
      star.glowAge += deltaSeconds;

      // Age all trail points and remove old ones in-place (avoids creating new array)
      let writeIdx = 0;
      for (let j = 0; j < star.trailPoints.length; j++) {
        const point = star.trailPoints[j];
        point.age += deltaSeconds;
        if (point.age < star.trailFadeTime) {
          star.trailPoints[writeIdx++] = point;
        }
      }
      star.trailPoints.length = writeIdx; // Truncate array in-place
    }

    // Update particles with spiraling motion (in-place removal to avoid GC pressure)
    let particleWriteIdx = 0;
    for (let j = 0; j < star.particles.length; j++) {
      const particle = star.particles[j];

      // Update spiral angle
      particle.angle += particle.angularVelocity * deltaSeconds;

      // Expand spiral radius outward
      particle.spiralRadius += particle.spiralSpeed * deltaSeconds;

      // Calculate spiral position relative to center
      const spiralX = Math.cos(particle.angle) * particle.spiralRadius;
      const spiralY = Math.sin(particle.angle) * particle.spiralRadius;

      // Update center position (moves with star's base velocity)
      particle.centerX += particle.vx * deltaSeconds;
      particle.centerY += particle.vy * deltaSeconds;

      // Final position = center + spiral offset
      particle.x = particle.centerX + spiralX;
      particle.y = particle.centerY + spiralY;

      particle.life -= deltaTime / (particle.maxLife * 1000);

      // Keep alive particles (in-place compaction)
      if (particle.life > 0) {
        star.particles[particleWriteIdx++] = particle;
      }
    }
    star.particles.length = particleWriteIdx; // Truncate array in-place

    // Remove star if trail is completely faded, glow has faded, and no particles remain
    const glowFaded = star.isDead && star.glowAge >= star.glowFadeTime;
    if (star.isDead && star.trailPoints.length === 0 && star.particles.length === 0 && glowFaded) {
      state.stars.splice(i, 1);
      continue;
    }

    // Remove stars that are completely off-screen (with large margin)
    // BUG FIX: Changed && to || - a position can't be both < -margin AND > width+margin
    const margin = 300;
    const isOffScreen = (x: number, y: number) =>
      x < -margin || x > canvas.width + margin ||
      y < -margin || y > canvas.height + margin;

    const starOffScreen = isOffScreen(star.x, star.y);
    const allTrailOffScreen = star.trailPoints.length === 0 ||
      star.trailPoints.every((p) => isOffScreen(p.x, p.y));
    const allParticlesOffScreen = star.particles.length === 0 ||
      star.particles.every((p) => isOffScreen(p.x, p.y));

    if (starOffScreen && allTrailOffScreen && allParticlesOffScreen) {
      state.stars.splice(i, 1);
    }
  }
}

export function renderShootingStars(
  ctx: CanvasRenderingContext2D,
  state: ShootingStarsState,
  parallaxOffset: number = 0
): void {
  // Get device pixel ratio for coordinate conversion
  const dpr = window.devicePixelRatio || 1;
  // Convert parallax offset from CSS to device pixels
  const parallaxOffsetDevice = parallaxOffset * dpr;
  // Viewport height in device pixels for culling
  const viewportHeightDevice = window.innerHeight * dpr;

  for (const star of state.stars) {
    // Calculate base alpha based on star life or trail fade
    // Stars get brighter as they die off (like real shooting stars)
    let starAlpha: number;
    let blurAmount: number = 0;
    let glowAlpha: number = 0;

    if (!star.isDead) {
      // Star is alive - get brighter as life decreases (inverse relationship)
      // life goes from 1.0 (full) to 0 (dead), so (1 - life) gives us 0 to 1
      const lifeRemaining = star.life; // 1.0 = full life, 0 = dead
      const brightnessBoost = (1 - lifeRemaining) * 0.5; // Up to 50% brighter as it dies
      // Increase brightness multiplier as life decreases, but still fade out at the very end
      starAlpha = Math.max(0, Math.min(1, star.brightness * (1 + brightnessBoost) * Math.max(0.3, lifeRemaining)));

      // Add blur/glow effect - stronger as star dies off
      blurAmount = (1 - lifeRemaining) * star.size * 4; // Blur increases as star dies (up to 4x size)
      glowAlpha = starAlpha;
    } else if (star.glowAge < star.glowFadeTime) {
      // Star is dead, but glow persists for 1 second
      const glowFadeProgress = star.glowAge / star.glowFadeTime; // 0 to 1
      glowAlpha = star.brightness * (1 - glowFadeProgress); // Fade from full brightness to 0
      blurAmount = star.size * 4 * (1 - glowFadeProgress); // Blur fades with glow
      starAlpha = 0; // Star head is gone, only glow remains
    } else {
      // Star is dead and glow has faded
      starAlpha = 0;
      blurAmount = 0;
      glowAlpha = 0;
    }

    // Render trail from history points
    if (star.trailPoints.length > 1) {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Draw trail segments with fading
      for (let i = 1; i < star.trailPoints.length; i++) {
        const prevPoint = star.trailPoints[i - 1];
        const currentPoint = star.trailPoints[i];

        // Apply parallax to both points (in device pixels)
        const prevY = prevPoint.y + parallaxOffsetDevice;
        const currY = currentPoint.y + parallaxOffsetDevice;

        // Calculate alpha based on age (for dead stars) or position (for living stars)
        let segmentAlpha: number;
        if (star.isDead) {
          // Trail fades based on age - fade out quickly and dimly
          const fadeProgress = currentPoint.age / star.trailFadeTime;
          segmentAlpha = (1 - fadeProgress) * star.brightness * 0.15; // Very dim trail that fades quickly (reduced from 0.3)
        } else {
          // Trail fades based on distance from head (newer = brighter) - reduced brightness
          const distanceFromHead = star.trailPoints.length - i;
          const maxDistance = star.trailPoints.length;
          segmentAlpha = starAlpha * (1 - distanceFromHead / maxDistance * 0.6) * 0.4; // Reduced by 0.4 multiplier
        }

        segmentAlpha = Math.max(0, Math.min(1, segmentAlpha));

        // Add viewport culling - skip segments that are completely off-screen
        // This prevents rendering trails above/below the viewport (in device pixels)
        const margin = 10 * dpr;
        const isInViewport = (prevY >= -margin && prevY <= viewportHeightDevice + margin) ||
                            (currY >= -margin && currY <= viewportHeightDevice + margin);

        if (segmentAlpha > 0.01 && isInViewport) {
          // Create gradient for this segment
          const segmentLength = Math.sqrt(
            Math.pow(currentPoint.x - prevPoint.x, 2) +
            Math.pow(currY - prevY, 2)
          );

          if (segmentLength > 0) {
            const gradient = ctx.createLinearGradient(
              prevPoint.x,
              prevY,
              currentPoint.x,
              currY
            );

            const headAlpha = segmentAlpha;
            const tailAlpha = segmentAlpha * 0.1; // Reduced from 0.4 to 0.2 for dimmer tail

            gradient.addColorStop(
              0,
              `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${headAlpha})`
            );
            gradient.addColorStop(
              1,
              `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${tailAlpha})`
            );

            ctx.strokeStyle = gradient;
            ctx.lineWidth = 2 + (segmentAlpha * 2); // Thicker for brighter segments

            ctx.beginPath();
            ctx.moveTo(prevPoint.x, prevY);
            ctx.lineTo(currentPoint.x, currY);
            ctx.stroke();
          }
        }
      }
    }

    // Render particles along the trail with spiraling effect
    for (const particle of star.particles) {
      const particleAlpha = (particle.life / particle.maxLife) * star.brightness * 0.7;
      const particleY = particle.y + parallaxOffsetDevice;
      const particleMargin = 10 * dpr;

      if (particleAlpha > 0.01 && particleY >= -particleMargin && particleY <= viewportHeightDevice + particleMargin) {
        // Add slight glow to spiraling particles
        ctx.shadowBlur = particle.size * 2;
        ctx.shadowColor = `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${particleAlpha * 0.5})`;

        ctx.fillStyle = `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${particleAlpha})`;
        ctx.beginPath();
        ctx.arc(particle.x, particleY, particle.size, 0, Math.PI * 2);
        ctx.fill();

        // Reset shadow
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
      }
    }

    // Draw bright head/glow (if star is alive or glow is still fading)
    if ((!star.isDead && starAlpha > 0.1) || (star.isDead && glowAlpha > 0.01)) {
      const renderY = star.y + parallaxOffsetDevice;
      const headMargin = 10 * dpr;

      // Only render if in viewport (in device pixels)
      if (renderY >= -headMargin && renderY <= viewportHeightDevice + headMargin) {
        const alpha = !star.isDead ? starAlpha : glowAlpha;
        const glowColor = `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${alpha})`;

        // Draw blur/glow effect (stronger as star dies off, or fading glow after death)
        if (blurAmount > 0) {
          ctx.shadowBlur = blurAmount;
          ctx.shadowColor = glowColor;
        } else {
          ctx.shadowBlur = star.size * 2; // Base blur
          ctx.shadowColor = glowColor;
        }

        // Draw outer glow layer (larger, more transparent)
        if (blurAmount > 0) {
          ctx.fillStyle = `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${alpha * 0.3})`;
          ctx.beginPath();
          ctx.arc(star.x, renderY, star.size * 1.5, 0, Math.PI * 2);
          ctx.fill();
        }

        // Draw main star head (only if star is alive)
        if (!star.isDead) {
          ctx.fillStyle = glowColor;
          ctx.beginPath();
          ctx.arc(star.x, renderY, star.size, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Draw glow only (no solid head) when star is dead
          ctx.fillStyle = glowColor;
          ctx.beginPath();
          ctx.arc(star.x, renderY, star.size * 0.8, 0, Math.PI * 2);
          ctx.fill();
        }

        // Reset shadow
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
      }
    }
  }
}
